import React, { useState, useEffect } from "react";
import Layout from "./Layout";
import "../css/BlogAnalysisRegistry.css";
import "material-icons/iconfont/material-icons.css";
import {
  fetchBlogAnalysisRegistry,
  fetchBlogAnalysisIndexTiers,
} from "../api/blogAnalysisRegistry";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}.${mm}.${dd} ${hh}:${min}`;
};

const SUMMARY_CARDS = [
  { key: "total", icon: "storage", label: "전체 데이터 수" },
  { key: "influencer", icon: "verified", label: "인플루언서 인증" },
  { key: "mate", icon: "grade", label: "메이트 인증" },
  { key: "avgScore", icon: "bar_chart", label: "평균 평가 점수" },
];

function BlogAnalysisRegistry() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [mateCount, setMateCount] = useState(null);
  const [influencerCount, setInfluencerCount] = useState(null);
  const [averageScore, setAverageScore] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [selectedTier, setSelectedTier] = useState("");
  const [certFilter, setCertFilter] = useState("all");
  const [indexTiers, setIndexTiers] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const limit = 50;

  const getCertParams = () => {
    if (certFilter === "influencer") return { isInfluencer: true };
    if (certFilter === "mate") return { isNaverMate: true };
    if (certFilter === "none")
      return { isInfluencer: false, isNaverMate: false };
    return {};
  };

  // Bug #6: indexTiers fallback — resolve됐지만 필드가 없는 경우 방어
  useEffect(() => {
    fetchBlogAnalysisIndexTiers()
      .then((res) => setIndexTiers(res.data?.indexTiers ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let ignore = false;
    const offset = (page - 1) * limit;
    setLoading(true);
    setError(null);
    fetchBlogAnalysisRegistry({
      keyword: appliedKeyword || undefined,
      indexTier: selectedTier || undefined,
      ...getCertParams(),
      limit,
      offset,
    })
      .then((res) => {
        if (ignore) return;
        setItems(res.data.items ?? []);
        setTotal(res.data.total ?? 0);
        setMateCount(res.data.mateCount ?? null);
        setInfluencerCount(res.data.influencerCount ?? null);
        setAverageScore(res.data.averageScore ?? null);
      })
      .catch((err) => {
        if (ignore) return;
        setError(err);
        setItems([]);
        setTotal(0);
        setMateCount(null);
        setInfluencerCount(null);
        setAverageScore(null);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [appliedKeyword, selectedTier, certFilter, page]);

  // Bug #4: page 리셋을 핸들러에서 직접 처리 → 별도 useEffect 제거
  const handleSearch = () => {
    setPage(1);
    setAppliedKeyword(keyword.trim());
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleTierChange = (e) => {
    setPage(1);
    setSelectedTier(e.target.value);
  };

  const handleResetFilters = () => {
    setPage(1);
    setSelectedTier("");
    setCertFilter("all");
    setKeyword("");
    setAppliedKeyword("");
  };

  const summaryValues = {
    total: total.toLocaleString(),
    influencer:
      influencerCount != null ? influencerCount.toLocaleString() : "-",
    mate: mateCount != null ? mateCount.toLocaleString() : "-",
    avgScore: averageScore != null ? `${averageScore}점` : "-",
  };

  const summarySubValues = {
    total: "실시간 분석 축적건",
    influencer:
      total > 0 && influencerCount != null
        ? `전체 데이터의 ${((influencerCount / total) * 100).toFixed(0)}%`
        : null,
    mate:
      total > 0 && mateCount != null
        ? `전체 데이터의 ${((mateCount / total) * 100).toFixed(0)}%`
        : null,
    avgScore: "유효 점수 종합 평균",
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
      document
        .querySelector(".main-content")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const handleNextPage = () => {
    const totalPages = Math.ceil(total / limit);
    if (page < totalPages) {
      setPage(page + 1);
      document
        .querySelector(".main-content")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const handlePageClick = (pageNum) => {
    setPage(pageNum);
    document
      .querySelector(".main-content")
      ?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageNumbers = () => {
    const pages = [];
    const totalPages = Math.ceil(total / limit);
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page <= 4) {
        for (let i = 2; i <= 5; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push("...");
        for (let i = page - 1; i <= page + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const offset = (page - 1) * limit;
  const startIndex = total === 0 ? 0 : offset + 1;
  const endIndex = Math.min(offset + limit, total);

  return (
    <Layout>
      <div className="blog-analysis-registry-container">
        {/* 요약 카드 */}
        <div className="summary-cards">
          {SUMMARY_CARDS.map((card) => (
            <div
              key={card.key}
              className={`summary-card summary-card--${card.key}`}
            >
              <div className="summary-icon-wrap">
                <span className="material-icons summary-icon">{card.icon}</span>
              </div>
              <div className="summary-content">
                <div className="summary-value">{summaryValues[card.key]}</div>
                <div className="summary-label">{card.label}</div>
                {summarySubValues[card.key] && (
                  <div className="summary-sub">
                    {card.key === "total" && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        style={{
                          marginRight: 3,
                          flexShrink: 0,
                          verticalAlign: "middle",
                          marginTop: "-3px",
                        }}
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                      </svg>
                    )}
                    {summarySubValues[card.key]}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* 검색 + 필터 (한 줄) */}
        <div className="filter-section">
          <input
            type="text"
            className="form-control search-input"
            placeholder="블로그 주소 또는 ID 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button className="btn search-btn" onClick={handleSearch}>
            조회
          </button>
          <select
            className="form-select filter-select"
            value={selectedTier}
            onChange={handleTierChange}
          >
            <option value="">전체 (전체 등급)</option>
            {indexTiers.map((tier) => (
              <option key={tier} value={tier}>
                {tier}
              </option>
            ))}
          </select>
          <label className="cert-label">공식 인증:</label>
          <select
            className="form-select filter-select"
            value={certFilter}
            onChange={(e) => setCertFilter(e.target.value)}
          >
            <option value="all">전체 보기</option>
            <option value="influencer">인플루언서</option>
            <option value="mate">메이트</option>
            <option value="none">없음</option>
          </select>
          <button className="btn reset-btn" onClick={handleResetFilters}>
            필터 초기화
          </button>
        </div>

        {/* 테이블 */}
        {loading ? (
          <div className="text-center py-4">로딩 중...</div>
        ) : error ? (
          <div className="text-center py-4 text-danger">
            데이터를 불러오는 중 오류가 발생했습니다.
          </div>
        ) : (
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>조회한 회원명</th>
                <th>블로그 주소</th>
                <th>블로그명</th>
                <th>마지막 분석 요청일</th>
                <th>크롤 시각</th>
                <th>평가 등급</th>
                <th>평가 점수</th>
                <th>공식 인증</th>
                <th>메이트 점수</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-3">
                    조회된 블로그가 없습니다.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.blogId}>
                    <td>{item.userName ?? "-"}</td>
                    <td className="blog-url-cell">
                      <a
                        href={item.blogUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.blogUrl}
                      </a>
                      <div className="blog-id-text">ID: {item.blogId}</div>
                    </td>
                    <td>{item.blogName ?? "-"}</td>
                    <td>{formatDate(item.lastAnalyzedAt)}</td>
                    <td>{formatDate(item.lastCrawledAt)}</td>
                    <td>{item.indexTier ?? "-"}</td>
                    <td>
                      {item.totalScore != null ? `${item.totalScore}점` : "-"}
                    </td>
                    <td>
                      {!item.isInfluencer && !item.isNaverMate && (
                        <span className="badge bg-secondary">없음</span>
                      )}
                      {item.isInfluencer && (
                        <span className="badge badge-influencer me-1">
                          인플루언서
                        </span>
                      )}
                      {item.isNaverMate && (
                        <span className="badge bg-success">메이트</span>
                      )}
                    </td>
                    <td>
                      {item.mateScore != null ? `${item.mateScore}점` : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {total > 0 && (
          <div className="pagination-info mt-3 mb-3">
            <p className="text-center text-muted small mb-2">
              총 {total.toLocaleString()}건 중 {startIndex.toLocaleString()} –{" "}
              {endIndex.toLocaleString()} 표시
            </p>
            <nav>
              <ul className="pagination justify-content-center mb-0">
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={handlePrevPage}
                    disabled={page === 1}
                  >
                    이전
                  </button>
                </li>
                {getPageNumbers().map((pageNum, index) =>
                  pageNum === "..." ? (
                    <li
                      key={`ellipsis-${index}`}
                      className="page-item disabled"
                    >
                      <span className="page-link">…</span>
                    </li>
                  ) : (
                    <li
                      key={pageNum}
                      className={`page-item ${page === pageNum ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageClick(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  ),
                )}
                <li
                  className={`page-item ${endIndex >= total ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={handleNextPage}
                    disabled={endIndex >= total}
                  >
                    다음
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default BlogAnalysisRegistry;
